import { createFilterOptions } from "@mui/material";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useNormalizedCompanyUsers } from "hooks/useNormalizedCompanyUsers";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { putAgreementPublisherSigner } from "store/agreementsStore";
import { useAppDispatch } from "store/store";
import type { IUser } from "types";

import { useAgreementPageContext } from "../context/StateProvider";
import styles from "./style.module.scss";

interface IChoosePublisherSignerModal {
    open: boolean;
    onClose: () => void;
    modalTitle: string;
    modalSubtitle: string;
}

const ChoosePublisherSignerModal = ({ open, onClose, modalTitle, modalSubtitle }: IChoosePublisherSignerModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const { companyInfos } = useCompanySettings();
    const { agreementPublisherSigner, agreement, hasAbilityToEditPublisherSigner } = useAgreementPageContext();
    const [selectedUser, setSelectedUser] = useState<IUser | null>(agreementPublisherSigner ?? null);

    const normalizedUsers = useNormalizedCompanyUsers();
    const userList = useMemo(
        () =>
            normalizedUsers.map((user) => ({
                ...user,
                label: `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim(),
                description: user.email,
            })),
        [normalizedUsers],
    );

    useEffect(() => {
        if (open) setSelectedUser(agreementPublisherSigner ?? null);
    }, [open, agreementPublisherSigner]);

    const filterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option: IUser & { label?: string }) =>
            `${option.firstname ?? ""} ${option.lastname ?? ""} ${option.email ?? ""} ${
                option.label ?? ""
            }`.toLowerCase(),
    });

    const handleValidatePublisherSigner = async () => {
        if (!selectedUser?.id) return;
        setIsLoading(true);
        try {
            await dispatch(
                putAgreementPublisherSigner({
                    agreementId: agreement?.id as number,
                    publisherSignerId: Number(selectedUser?.id),
                }),
            );
        } finally {
            onClose();
            setIsLoading(false);
        }
    };

    /**
     * Security check
     */
    if (!hasAbilityToEditPublisherSigner) {
        return null;
    }

    return (
        <ModalBase open={open} onClose={onClose} modalTitle={modalTitle} modalSubtitle={modalSubtitle}>
            <div className={styles["choose-publisher-signer"]}>
                <div className={styles["choose-publisher-signer-field"]}>
                    <div className={styles["choose-publisher-signer-label"]}>
                        <BodyHighlight>{t("ContractValidationEdit.InternalSignatoryLabel")}</BodyHighlight>
                        <SmallBody color="grey">({companyInfos?.name ?? ""})</SmallBody>
                    </div>
                    <AutocompleteFormComponent
                        sx={{ width: "100%" }}
                        options={userList}
                        value={selectedUser}
                        onChange={(_e, data) => setSelectedUser(data)}
                        renderOptionText={(option) =>
                            option?.label ?? `${option?.firstname ?? ""} ${option?.lastname ?? ""}`
                        }
                        placeholder={t("ContractValidation.ChoseSignatory")}
                        filterOptions={filterOptions}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    />
                </div>
                <div className={styles["choose-publisher-signer-actions"]}>
                    <StyledButton
                        onClick={handleValidatePublisherSigner}
                        buttonType="primary"
                        disabled={!selectedUser?.id}
                        loading={isLoading}
                        fullWidth
                    >
                        {t("ContractValidationEdit.ValidatePublisherSigner")}
                    </StyledButton>
                </div>
            </div>
        </ModalBase>
    );
};

export default ChoosePublisherSignerModal;
